import {
  capitalize,
  distinctArray,
  isValidFileName,
} from '@elementary/common-ui/utils/common';
import YAML from 'js-yaml';
import { Path, PathValue, UseFormReturn, UseFormWatch } from 'react-hook-form';
import { WarehouseType } from '@elementary/common-ui/types';
import {
  conditionalOperators,
  getTimeBucketUnitOptions,
  TRAINING_PERIOD_LIMITS,
  trainingPeriodOptions,
} from 'shared/constants';
import {
  AddTestForm,
  ConditionalOperator,
  Severity,
  Test,
  TestManagementGqlTest,
  TestTemplateMetadata,
} from 'shared/types';
import { parseConditionalSeverity } from 'shared/utils/utils';
import { getAnomalyTimeWindowError } from 'shared/utils/anomalyTimeWindow';
import {
  WizardMode,
  WizardResourceType,
  WizardSelectableTest,
  WizardStageInfo,
} from 'testManagement/types';
import {
  getNoTimestampColumnMessage,
  getTimestampFieldRequirement,
  TIME_BUCKET_ONLY_FIELDS,
  TIME_BUCKET_ONLY_REASON,
  usesTimeBuckets,
} from './wizardTimestampFields';

export const isWizardResourceType = (
  value?: string,
): value is WizardResourceType =>
  value === 'tables' || value === 'columns' || value === 'customQuery';

export const getWizardStages = ({
  wizardMode,
  wizardResourceType,
}: {
  wizardMode: WizardMode;
  wizardResourceType: WizardResourceType;
}): WizardStageInfo[] => {
  if (wizardResourceType === 'customQuery') {
    return [
      {
        index: 1,
        name: 'Query & configuration',
        hint: 'Enter and validate a query, then complete the form to submit',
      },
    ];
  }

  if (wizardMode === 'plan') {
    return [
      {
        index: 1,
        name: 'Test configuration & attributes',
        hint: 'Complete the form to save changes to the plan',
      },
    ];
  }

  if (wizardMode === 'edit') {
    return [
      {
        index: 1,
        name: 'Test configuration & attributes',
        hint: 'Modify the configuration to continue',
      },
    ];
  }

  return [
    {
      index: 1,
      name: 'Select target',
      hint: 'Select a target to continue',
    },
    {
      index: 2,
      name: 'Select a test',
      hint: 'Select a test to continue',
    },
    {
      index: 3,
      name: 'Test configuration & attributes',
      hint: 'Complete the form to continue',
    },
  ];
};

export const getWizardStage = (
  stages: WizardStageInfo[],
  index: number,
): WizardStageInfo =>
  stages.find((stage) => stage.index === index) ?? stages[0];

const wizardFieldTopLevel = (field: string) => field.split('.')[0] ?? field;

export const toWizardSelectableTest = (
  testTemplate: TestTemplateMetadata,
  source: 'api' | 'elementary',
): WizardSelectableTest => {
  const [packageName, testName] = testTemplate.test_name.split('.');
  const testType =
    source === 'api'
      ? packageName === 'elementary_cloud'
        ? `cloud${testName
            .split('_')
            .map((word) => capitalize(word))
            .join('')}`
        : 'yamlTest'
      : testName
          .split('_')
          .map((word, index) => (index > 0 ? capitalize(word) : word))
          .join('');
  const yamlTemplateName =
    testType === 'yamlTest' ? testTemplate.test_name : undefined;
  const name =
    packageName === 'elementary_cloud' ? `cloud_${testName}` : testName;

  return {
    key: yamlTemplateName ?? testType,
    testType,
    ...(yamlTemplateName ? { yamlTemplateName } : {}),
    name,
    package: packageName?.toUpperCase(),
    ...(testTemplate.quality_dimension
      ? { qualityDimension: testTemplate.quality_dimension }
      : {}),
    ...(testTemplate.coming_soon ? { comingSoon: true } : {}),
  };
};

export const getWizardSelectableTests = ({
  apiTemplates,
  elementaryTests,
}: {
  apiTemplates?: TestTemplateMetadata[];
  elementaryTests: TestTemplateMetadata[];
}): WizardSelectableTest[] => [
  ...(apiTemplates ?? []).map((test) => toWizardSelectableTest(test, 'api')),
  ...elementaryTests.map((test) => toWizardSelectableTest(test, 'elementary')),
];

const isPlainObject = (value: unknown): value is object =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringKeyRecord = (value: unknown): value is Record<string, unknown> =>
  isPlainObject(value);

const WIZARD_ARRAY_FIELDS = new Set([
  'resources',
  'owners',
  'tags',
  'columnGroup',
  'values',
  'baselineColumns',
]);

export const coerceWizardStringList = (
  value: unknown,
): string[] | undefined => {
  if (Array.isArray(value)) {
    return value.every((item) => typeof item === 'string') ? value : undefined;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (
        Array.isArray(parsed) &&
        parsed.every((item) => typeof item === 'string')
      ) {
        return parsed;
      }
    } catch {
      return undefined;
    }
    return undefined;
  }
  return trimmed
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

export const getWizardWriteFieldError = (
  field: string,
  value: unknown,
): string | undefined => {
  if (field === 'owners' || field === 'tags' || field === 'resources') {
    return coerceWizardStringList(value)
      ? undefined
      : `${field} must be an array of strings`;
  }
  if (WIZARD_ARRAY_FIELDS.has(field) && !Array.isArray(value)) {
    return `${field} must be an array`;
  }
  return undefined;
};

const SET_VALUE_OPTS = { shouldDirty: true, shouldValidate: true } as const;

const PERIOD_UNITS: Record<string, string> = {
  day: 'day',
  days: 'day',
  hour: 'hour',
  hours: 'hour',
  week: 'week',
  weeks: 'week',
  minute: 'minute',
  minutes: 'minute',
};

const ANOMALY_SENSITIVITIES = new Set(['low', 'medium', 'high']);
const ANOMALY_DIRECTIONS = new Set(['BOTH', 'DROP', 'SPIKE']);

const parseConditionalOperator = (
  value: unknown,
): ConditionalOperator | undefined =>
  conditionalOperators.find((operator) => operator === value);

const readStringField = (
  value: Record<string, unknown>,
  keys: readonly string[],
): string | undefined => {
  const match = keys
    .map((key) => value[key])
    .find((item) => typeof item === 'string' && item.trim());
  return typeof match === 'string' ? match : undefined;
};

const coerceSeverityType = (value: unknown): Severity | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.toLowerCase();
  if (normalized === 'error') {
    return 'error';
  }
  if (normalized === 'warn' || normalized === 'warning') {
    return 'warn';
  }
  return undefined;
};

const coerceSeverityCondition = (
  value: unknown,
): { operator?: ConditionalOperator; value?: number } | undefined => {
  if (!isStringKeyRecord(value)) {
    return undefined;
  }
  const operator = parseConditionalOperator(value.operator);
  const rawValue = value.value;
  const numericValue =
    typeof rawValue === 'number'
      ? rawValue
      : typeof rawValue === 'string' && rawValue.trim() !== ''
      ? Number(rawValue)
      : undefined;
  if (!operator && (numericValue === undefined || Number.isNaN(numericValue))) {
    return undefined;
  }
  return {
    ...(operator ? { operator } : {}),
    ...(numericValue !== undefined && !Number.isNaN(numericValue)
      ? { value: numericValue }
      : {}),
  };
};

const DEFAULT_CONDITIONAL_ERROR: {
  operator: ConditionalOperator;
  value: number;
} = { operator: '>', value: 0 };

export const coerceWizardSeverity = (
  value: unknown,
): AddTestForm['severity'] | undefined => {
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (normalized === 'error') return { type: 'error' };
    if (normalized === 'warn' || normalized === 'warning')
      return { type: 'warn' };
    if (normalized === 'conditional') {
      return { type: 'error', conditionalError: DEFAULT_CONDITIONAL_ERROR };
    }
    return undefined;
  }
  if (!isStringKeyRecord(value)) {
    return undefined;
  }
  const type = coerceSeverityType(value.type);
  const errorIf = readStringField(value, ['error_if', 'errorIf']);
  const warnIf = readStringField(value, ['warn_if', 'warnIf']);
  const nestedError = coerceSeverityCondition(value.conditionalError);
  const nestedWarning = coerceSeverityCondition(value.conditionalWarning);
  const wantsConditional =
    (typeof value.type === 'string' &&
      value.type.toLowerCase() === 'conditional') ||
    !!errorIf ||
    !!warnIf ||
    !!nestedError ||
    !!nestedWarning;

  if (wantsConditional) {
    const parsed = parseConditionalSeverity(type ?? 'error', errorIf, warnIf);
    const conditionalError =
      nestedError ??
      parsed.conditionalError ??
      (errorIf || warnIf || nestedWarning
        ? undefined
        : DEFAULT_CONDITIONAL_ERROR);
    const conditionalWarning = nestedWarning ?? parsed.conditionalWarning;
    return {
      type: type ?? parsed.type ?? 'error',
      ...(conditionalError ? { conditionalError } : {}),
      ...(conditionalWarning ? { conditionalWarning } : {}),
    };
  }
  if (type) {
    return { type };
  }
  return undefined;
};

const coercePeriodUnit = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  return PERIOD_UNITS[value.toLowerCase()];
};

const coercePeriod = (value: unknown, current: unknown): unknown => {
  const currentPeriod = isPlainObject(current) ? current : {};
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return {
      ...currentPeriod,
      count: value,
      period:
        (currentPeriod as { period?: unknown }).period ??
        coercePeriodUnit('day') ??
        'day',
    };
  }
  if (!isPlainObject(value)) {
    return value;
  }
  const period = coercePeriodUnit((value as { period?: unknown }).period);
  return {
    ...currentPeriod,
    ...value,
    ...(period ? { period } : {}),
  };
};

const coerceAnomalyConfig = (value: unknown, current: unknown): unknown => {
  if (!isPlainObject(value)) {
    return value;
  }
  const currentConfig = isPlainObject(current) ? current : {};
  const next: Record<string, unknown> = {
    ...(currentConfig as Record<string, unknown>),
  };
  Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
    if (key === 'sensitivity' && typeof child === 'string') {
      const sensitivity = child.toLowerCase();
      if (ANOMALY_SENSITIVITIES.has(sensitivity)) {
        next.sensitivity = sensitivity;
        return;
      }
    }
    if (key === 'anomalyDirection' && typeof child === 'string') {
      const direction = child.toUpperCase();
      if (ANOMALY_DIRECTIONS.has(direction)) {
        next.anomalyDirection = direction;
        return;
      }
    }
    if (
      key === 'trainingPeriod' ||
      key === 'detectionPeriod' ||
      key === 'fixedThreshold' ||
      key === 'failureThresholdGrace'
    ) {
      next[key] = coercePeriod(
        child,
        (currentConfig as Record<string, unknown>)[key],
      );
      return;
    }
    next[key] = child;
  });
  return next;
};

const WIZARD_YAML_CONFIG_KEYS_TO_OMIT = [
  'store_failures',
  'tags',
  'meta',
  'error_if',
  'warn_if',
];

export const extractWizardSeverityFromYaml = (
  yaml: string,
): AddTestForm['severity'] | undefined => {
  let parsed: unknown;
  try {
    parsed = YAML.load(yaml);
  } catch {
    return undefined;
  }
  if (!Array.isArray(parsed)) {
    return undefined;
  }
  let errorIf: string | undefined;
  let warnIf: string | undefined;
  parsed.forEach((item) => {
    if (!isStringKeyRecord(item)) {
      return;
    }
    Object.values(item).forEach((body) => {
      if (!isStringKeyRecord(body) || !isStringKeyRecord(body.config)) {
        return;
      }
      errorIf =
        readStringField(body.config, ['error_if', 'errorIf']) ?? errorIf;
      warnIf = readStringField(body.config, ['warn_if', 'warnIf']) ?? warnIf;
    });
  });
  if (!errorIf && !warnIf) {
    return undefined;
  }
  return coerceWizardSeverity({ error_if: errorIf, warn_if: warnIf });
};

export const omitDisallowedWizardYamlConfig = (yaml: string): string => {
  let parsed: unknown;
  try {
    parsed = YAML.load(yaml);
  } catch {
    return yaml;
  }
  if (!Array.isArray(parsed)) {
    return yaml;
  }
  let changed = false;
  parsed.forEach((item) => {
    if (!isStringKeyRecord(item)) {
      return;
    }
    Object.values(item).forEach((body) => {
      if (!isStringKeyRecord(body)) {
        return;
      }
      const { config } = body;
      if (!isStringKeyRecord(config)) {
        return;
      }
      WIZARD_YAML_CONFIG_KEYS_TO_OMIT.forEach((key) => {
        if (key in config) {
          delete config[key];
          changed = true;
        }
      });
    });
  });
  if (!changed) {
    return yaml;
  }
  return YAML.dump(parsed, { lineWidth: -1 });
};

export const getUnselectableWizardResourcesReason = (
  resources: unknown,
  selectableResourceIds: string[],
): string | undefined => {
  if (!Array.isArray(resources)) {
    return undefined;
  }
  const selectable = new Set(selectableResourceIds);
  const unknown = resources.filter(
    (id): id is string => typeof id === 'string' && !selectable.has(id),
  );
  if (unknown.length === 0) {
    return undefined;
  }
  return `Not in this wizard's table list, so they cannot be selected: ${unknown.join(
    ', ',
  )}. Internal Elementary models and assets that cannot have tests are not listed.`;
};

const coerceWizardTimeUnit = (
  field: string,
  value: unknown,
  options: { value: string }[],
): { ok: true; value: string } | { ok: false; reason: string } => {
  const allowed = options.map((option) => option.value);
  const normalized = typeof value === 'string' ? value.toLowerCase() : '';
  const unit = allowed.find(
    (option) => normalized === option || normalized === `${option}s`,
  );
  if (unit) {
    return { ok: true, value: unit };
  }
  return {
    ok: false,
    reason: `${field} must be one of ${allowed.join(', ')}`,
  };
};

export const normalizeWizardWriteFieldValue = (
  field: string,
  value: unknown,
  current: unknown,
  warehouseType?: WarehouseType,
): { ok: true; value: unknown } | { ok: false; reason: string } => {
  if (field === 'detectionUnit') {
    return coerceWizardTimeUnit(
      field,
      value,
      getTimeBucketUnitOptions(warehouseType),
    );
  }
  if (field === 'trainingPeriodUnit') {
    return coerceWizardTimeUnit(field, value, trainingPeriodOptions);
  }
  if (field === 'owners' || field === 'tags' || field === 'resources') {
    const list = coerceWizardStringList(value);
    if (!list) {
      return { ok: false, reason: `${field} must be an array of strings` };
    }
    return { ok: true, value: list };
  }
  if (field === 'severity') {
    const severity = coerceWizardSeverity(value);
    if (!severity) {
      return {
        ok: false,
        reason:
          'severity must be warn, warning, error, conditional, or { type, conditionalError, conditionalWarning }',
      };
    }
    return { ok: true, value: severity };
  }
  if (field === 'anomalyConfig') {
    if (!isPlainObject(value)) {
      return { ok: false, reason: 'anomalyConfig must be an object' };
    }
    return { ok: true, value: coerceAnomalyConfig(value, current) };
  }
  if (field === 'yaml' && typeof value === 'string') {
    return { ok: true, value: omitDisallowedWizardYamlConfig(value) };
  }
  return { ok: true, value };
};

const setWizardValueDeep = (
  methods: UseFormReturn<AddTestForm>,
  field: string,
  value: unknown,
): void => {
  methods.setValue(
    field as Path<AddTestForm>,
    value as PathValue<AddTestForm, Path<AddTestForm>>,
    SET_VALUE_OPTS,
  );
  if (isPlainObject(value) && Object.keys(value).length > 0) {
    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
      setWizardValueDeep(methods, `${field}.${key}`, child);
    });
  }
};

export const setWizardFormField = (
  methods: UseFormReturn<AddTestForm>,
  field: string,
  value: unknown,
): void => {
  setWizardValueDeep(methods, field, value);
};

const proposedField = (proposed: object, field: string): unknown =>
  Object.entries(proposed).find(([key]) => key === field)?.[1];

const wizardTestOptionMatchesToken = (
  option: WizardSelectableTest,
  token: string,
) => {
  const value = token.trim().toLowerCase();
  return (
    option.name.toLowerCase() === value ||
    option.testType.toLowerCase() === value ||
    option.key.toLowerCase() === value ||
    option.yamlTemplateName?.toLowerCase() === value
  );
};

export const resolveWizardTestSelection = (
  proposed: object,
  options: WizardSelectableTest[],
):
  | { ok: true; testType: AddTestForm['testType']; yamlTemplateName: string }
  | { ok: false; reason: string } => {
  const proposedTestType = proposedField(proposed, 'testType');
  const proposedYamlTemplateName = proposedField(proposed, 'yamlTemplateName');

  if (proposedTestType === '' || proposedTestType === null) {
    return { ok: true, testType: '', yamlTemplateName: '' };
  }

  const testType =
    typeof proposedTestType === 'string' ? proposedTestType : undefined;
  const yamlTemplateName =
    typeof proposedYamlTemplateName === 'string'
      ? proposedYamlTemplateName
      : undefined;

  const tokens = [
    testType && testType !== 'yamlTest' ? testType : undefined,
    yamlTemplateName,
  ].filter((token): token is string => !!token);

  const match =
    tokens.length === 0
      ? undefined
      : options.find((option) =>
          tokens.every((token) => wizardTestOptionMatchesToken(option, token)),
        );

  if (!match || match.comingSoon) {
    return {
      ok: false,
      reason:
        'Unknown test. Use a testTemplates entry from read_test_config_wizard: set testType to the entry testType, and yamlTemplateName when testType is yamlTest.',
    };
  }
  if (match.disabledReason) {
    return { ok: false, reason: match.disabledReason };
  }

  return {
    ok: true,
    testType: match.testType as AddTestForm['testType'],
    yamlTemplateName: match.yamlTemplateName ?? '',
  };
};

export const getWizardFieldLockReason = ({
  field,
  values,
  wizardMode,
  timestampColumns,
}: {
  field: string;
  values: Record<string, unknown>;
  wizardMode: WizardMode;
  timestampColumns?: string[];
}): string | undefined => {
  const name = wizardFieldTopLevel(field);

  const timestampRequirement = getTimestampFieldRequirement(
    name,
    values.testType,
  );
  if (timestampRequirement && timestampColumns?.length === 0) {
    return getNoTimestampColumnMessage(
      Array.isArray(values.resources) ? values.resources.length : 0,
      timestampRequirement === 'required',
    );
  }

  if (
    TIME_BUCKET_ONLY_FIELDS.has(name) &&
    !usesTimeBuckets(values.testType, values.timestampColumn)
  ) {
    return TIME_BUCKET_ONLY_REASON;
  }

  if (name === 'resourceType') {
    return 'Asset type is set by the page (table, column, or custom SQL), not writable';
  }

  if (wizardMode === 'edit' && name === 'yaml') {
    return 'The YAML editor is read-only while editing an existing test';
  }

  if (
    (wizardMode === 'edit' || wizardMode === 'plan') &&
    (name === 'resources' || name === 'testType' || name === 'yamlTemplateName')
  ) {
    return 'The selected test and target cannot be changed while editing';
  }

  if (name === 'testType' && values.testType === 'customQuery') {
    return 'This wizard is always a custom SQL test, not writable';
  }

  if (
    name === 'customSqlPrimaryAssetId' &&
    values.customQueryDestination === 'cloud' &&
    !values.customSqlValidated
  ) {
    return 'Run query to choose a tested table';
  }

  if (name === 'sampleRowLimit' && values.storeRowSample === false) {
    return 'Turn on store row sample to set a sample row limit';
  }

  return undefined;
};

const isTrainingPeriodValid = (watch: UseFormWatch<AddTestForm>): boolean => {
  const tp = watch('anomalyConfig.trainingPeriod');
  if (!tp || tp.count == null || isNaN(Number(tp.count))) return false;
  const limits = TRAINING_PERIOD_LIMITS[tp.period];
  if (!limits) return false;
  return Number(tp.count) >= limits.min && Number(tp.count) <= limits.max;
};

const isConfigStageIndex = (
  watch: UseFormWatch<AddTestForm>,
  index: number,
  isEditMode?: boolean,
) =>
  isEditMode
    ? index === 1
    : watch('testType') === 'customQuery'
    ? index === 1
    : index === 3;

const LEGACY_ANOMALY_TEST_TYPES = new Set([
  'freshnessAnomalies',
  'volumeAnomalies',
  'eventFreshnessAnomalies',
  'dimensionAnomalies',
]);

const getLegacyAnomalyTimeWindowError = (watch: UseFormWatch<AddTestForm>) =>
  getAnomalyTimeWindowError({
    detectionPeriod: watch('detectionPeriod'),
    detectionUnit: watch('detectionUnit'),
    trainingPeriodCount: watch('trainingPeriodCount'),
    trainingPeriodUnit: watch('trainingPeriodUnit'),
    seasonality: watch('seasonality'),
  });

const getRequiredAnomalyFieldError = (
  watch: UseFormWatch<AddTestForm>,
  timestampColumns?: string[],
): string | undefined => {
  const testType = watch('testType');
  if (
    getTimestampFieldRequirement('timestampColumn', testType) === 'required' &&
    !watch('timestampColumn')
  ) {
    if (timestampColumns?.length === 0) {
      return getNoTimestampColumnMessage(watch('resources')?.length ?? 0, true);
    }
    return testType === 'eventFreshnessAnomalies'
      ? 'Select an event timestamp column'
      : 'Select a timestamp column';
  }
  if (testType === 'dimensionAnomalies' && !watch('columnGroup')?.length) {
    return 'Add at least one column to group by';
  }
  return undefined;
};

export const getStageValidationError = (
  methods: UseFormReturn<AddTestForm, any, undefined>,
  index: number,
  isEditMode?: boolean,
  timestampColumns?: string[],
): string | undefined => {
  const { watch } = methods;
  if (
    !isConfigStageIndex(watch, index, isEditMode) ||
    !LEGACY_ANOMALY_TEST_TYPES.has(watch('testType'))
  ) {
    return undefined;
  }
  return (
    getRequiredAnomalyFieldError(watch, timestampColumns) ??
    (usesTimeBuckets(watch('testType'), watch('timestampColumn'))
      ? getLegacyAnomalyTimeWindowError(watch)
      : undefined)
  );
};

export const isStageValid = (
  methods: UseFormReturn<AddTestForm, any, undefined>,
  index: number,
  isEditMode?: boolean,
  isTestConfigEdited?: boolean,
  isTicketRequired?: boolean,
) => {
  const { watch } = methods;

  const isTargetSelectionStage =
    watch('testType') !== 'customQuery' && !isEditMode && index === 1;

  const isConfigStage = isConfigStageIndex(watch, index, isEditMode);

  const isSelectTestStage =
    !isEditMode && watch('testType') !== 'customQuery' && index === 2;

  if (isTargetSelectionStage) {
    return !!(watch('resources')?.length > 0);
  }

  if (isSelectTestStage) {
    return !!watch('testType');
  }

  if (isConfigStage) {
    if (isTicketRequired && !watch('ticketId')) return false;

    if (watch('testType') === 'customQuery') {
      return (
        !!watch('customQuery')?.trim() &&
        !!watch('customQueryConverted') &&
        isValidFileName(watch('testName')) &&
        !!watch('path')
      );
    }

    if (watch('testType') === 'yamlTest') {
      const isYamlValid = !!watch('yaml') && watch('yamlValid');

      if (isEditMode) {
        return isYamlValid && !!isTestConfigEdited;
      }
      return isYamlValid;
    }

    if (LEGACY_ANOMALY_TEST_TYPES.has(watch('testType'))) {
      return !getStageValidationError(methods, index, isEditMode);
    }

    if (watch('testType') === 'cloudVolumeAnomalies') {
      const commonValid = !!(
        watch('anomalyConfig.anomalyDirection') &&
        watch('severity')?.type &&
        Number(watch('anomalyConfig.detectionPeriod.count')) > 0 &&
        watch('anomalyConfig.detectionPeriod.period') &&
        isTrainingPeriodValid(watch)
      );
      if (!commonValid) return false;

      // Percentage-threshold mode hides (and unregisters) the sensitivity
      // dropdown and instead requires the spike/drop thresholds relevant to the
      // chosen direction; anomaly-detection mode requires the sensitivity.
      if (watch('volumeDetectionMode') === 'percentage_thresholds') {
        const direction = watch('anomalyConfig.anomalyDirection');
        const spike = Number(watch('anomalyConfig.spikeThreshold'));
        const drop = Number(watch('anomalyConfig.dropThreshold'));
        const spikeValid = direction === 'DROP' || spike > 0;
        const dropValid = direction === 'SPIKE' || (drop > 0 && drop <= 100);
        return spikeValid && dropValid;
      }
      return !!watch('anomalyConfig.sensitivity');
    }

    if (watch('testType') === 'cloudFreshnessAnomalies') {
      return !!(
        (watch('anomalyConfig.fixedThreshold') ||
          watch('anomalyConfig.sensitivity')) &&
        watch('severity')?.type &&
        isTrainingPeriodValid(watch)
      );
    }

    if (watch('testType') === 'cloudPipelineTaskPerformance') {
      if (watch('anomalyConfig.fixedThreshold') != null) {
        return !!(
          (watch('anomalyConfig.fixedThreshold.count') || 0) > 0 &&
          watch('severity')?.type
        );
      }
      return !!(
        watch('anomalyConfig.sensitivity') &&
        watch('severity')?.type &&
        isTrainingPeriodValid(watch)
      );
    }

    if (
      watch('testType') === 'schemaChanges' ||
      watch('testType') === 'unique' ||
      watch('testType') === 'notNull'
    ) {
      return true;
    }

    if (watch('testType') === 'cloudDataContract') {
      const mode = watch('baselineMode') || 'complete';
      if (mode !== 'manual') {
        return true;
      }
      const cols = watch('baselineColumns');
      return !!(
        Array.isArray(cols) &&
        cols.length > 0 &&
        cols.some((c) => c.column_name && c.data_type)
      );
    }

    if (watch('testType') === 'acceptedValues') {
      return watch('values')?.length > 0;
    }

    if (watch('testType') === 'relationship') {
      return !!watch('tableName') && !!watch('columnName');
    }
  }

  return false;
};

export const getConditionalSeverityValue = (
  watch: UseFormWatch<AddTestForm>,
):
  | {
      severity?: 'conditional';
      warn_if?: string;
      error_if?: string;
    }
  | undefined => {
  const { conditionalWarning, conditionalError } = watch('severity') || {};

  if (!conditionalWarning && !conditionalError) {
    return undefined;
  }

  const isDefaultValueSelected =
    !conditionalWarning &&
    conditionalError?.operator === '!=' &&
    conditionalError.value === 0;

  if (isDefaultValueSelected) {
    return undefined;
  }

  const resultValue: {
    warn_if?: string;
    error_if?: string;
  } = {};

  if (conditionalWarning?.value !== undefined && conditionalWarning?.operator) {
    resultValue.warn_if = `${conditionalWarning.operator}${conditionalWarning.value}`;
  }

  if (conditionalError?.value !== undefined && conditionalError?.operator) {
    resultValue.error_if = `${conditionalError.operator}${conditionalError.value}`;
  }

  return { ...resultValue, severity: 'conditional' };
};

const getLegacyAnomalyTimeWindowArgs = (data: AddTestForm) => ({
  time_bucket: usesTimeBuckets(data.testType, data.timestampColumn)
    ? { period: data.detectionUnit, count: Number(data.detectionPeriod) }
    : null,
  training_period: {
    period: data.trainingPeriodUnit,
    count: Number(data.trainingPeriodCount),
  },
});

const getBackfillDaysArg = (data: AddTestForm) =>
  usesTimeBuckets(data.testType, data.timestampColumn)
    ? data.backfillDays
    : null;

export const getTestArgs = (data: AddTestForm) => {
  switch (data.testType) {
    case 'freshnessAnomalies':
      return {
        timestamp_column: data.timestampColumn,
        ...getLegacyAnomalyTimeWindowArgs(data),
        sensitivity: data.sensitivity,
        backfill_days: data.backfillDays,
        where_expression: data.whereClause || null,
        seasonality: data.seasonality === 'yes' ? 'day_of_week' : null,
      };
    case 'volumeAnomalies':
      return {
        timestamp_column: data.timestampColumn || null,
        ...getLegacyAnomalyTimeWindowArgs(data),
        sensitivity: data.sensitivity,
        backfill_days: getBackfillDaysArg(data),
        where_expression: data.whereClause || null,
        seasonality: data.seasonality === 'yes' ? 'day_of_week' : null,
        anomaly_direction:
          data.detectionEventType === 'anomaly'
            ? null
            : data.detectionEventType,
      };
    case 'eventFreshnessAnomalies':
      return {
        event_timestamp_column: data.timestampColumn,
        update_timestamp_column: data.updatedAtTimestampColumn || null,
        ...getLegacyAnomalyTimeWindowArgs(data),
        sensitivity: data.sensitivity,
        backfill_days: data.backfillDays,
        where_expression: data.whereClause || null,
        seasonality: data.seasonality === 'yes' ? 'day_of_week' : null,
      };
    case 'dimensionAnomalies':
      return {
        timestamp_column: data.timestampColumn || null,
        ...getLegacyAnomalyTimeWindowArgs(data),
        sensitivity: data.sensitivity,
        backfill_days: getBackfillDaysArg(data),
        where_expression: data.whereClause || null,
        dimensions: data.columnGroup,
        seasonality: data.seasonality === 'yes' ? 'day_of_week' : null,
        anomaly_direction:
          data.detectionEventType === 'anomaly'
            ? null
            : data.detectionEventType,
      };
    case 'unique':
    case 'notNull':
      return {};
    case 'acceptedValues':
      return {
        values: data.values,
        quote: data.valuesAreNotStrings ? false : null,
      };
    case 'relationship':
      return {
        field: data.columnName,
        to: data.tableName,
      };
    default:
      return {};
  }
};

export const isOnlyCustomQueryTests = (tests: Test[]) =>
  tests.every((item: Test) => item.is_singular);

const CLOUD_SOURCE_TYPE = 'elementary_cloud';

export const normalizeTest = (test: TestManagementGqlTest): Test => {
  const isCloud = test.sourceType === CLOUD_SOURCE_TYPE;

  const alertDestinations = test.alertDestinations?.map((dest) => ({
    id: dest.id ?? undefined,
    channel: dest.channel,
    type: dest.type ?? undefined,
    configuration_type: dest.configurationType ?? undefined,
    rule_id: dest.ruleId ?? undefined,
  }));

  return {
    id: test.id,
    asset_id: test.tableAsset?.id,
    database_name: test.tableAsset?.dbName,
    schema: test.tableAsset?.schemaName ?? '',
    table: test.tableAsset?.tableName ?? '',
    model_name: test.tableAsset?.name,
    source_name: test.tableAsset?.sourceName ?? undefined,
    last_data_modification_time:
      test.tableAsset?.lastDataModificationTime ?? undefined,
    materialization: test.tableAsset?.materialization ?? undefined,
    column: test.columnName ?? undefined,
    // `namespace` is what the REST payload called `package`.
    package: isCloud
      ? 'elementary'
      : test.namespace === 'elementary'
      ? 'elementary'
      : 'dbt',
    name: test.name,
    description: test.description ?? undefined,
    type: isCloud ? test.type : test.testCategory ?? undefined,
    source_type: isCloud ? 'cloud' : 'dbt',
    severity: isCloud
      ? capitalize(test.severity.toLowerCase())
      : test.severity.toLowerCase(),
    tags: distinctArray([
      ...(test.tags || []),
      ...(test.tableAsset?.tags || []),
    ]),
    owners: distinctArray([
      ...(test.owners || []),
      ...(test.tableAsset?.owners || []),
    ]),
    args: test.args ?? {},
    updated_at: test.updatedAt ?? '',
    meta: test.testMetadata ?? {},
    is_singular: test.isSingular,
    is_custom_sql: test.isCustomSql,
    original_path: test.path ?? '',
    is_cloud: isCloud,
    muted: test.config?.muted,
    channels: alertDestinations?.map((dest) => dest.channel) ?? [],
    alert_destinations: alertDestinations,
    // Should be the same type for all destinations.
    alert_destinations_type:
      test.alertDestinations?.[0]?.configurationType ?? undefined,
  };
};
